//================================================================
//  Page: Final Approval 
//================================================================

//  Purpose: Contains table of Final Approvals

//  Properties:
//  - taskSection = {useState, determines whether this section is collapsed}
//  - task = {object, holds task data}

// Example:
//    <FinalApproval
//        taskSection={taskSection}
//        task={task}
//    ></FinalApproval>

//================================================================

//Libraries
import React, { useEffect, useState } from 'react';

//Contexts

//Components
import StatusLabel from '../../../../../Components/StatusLabel/StatusLabel';
import Tooltip from '../../../../../Components/Tooltip/Tooltip';
import SubmitFinalApprovalPane from './SidePanes/SubmitFinalApprovalPane';
import UserSearchLiveFieldsFinance from '../../../../../Components/LiveField/Components/UserSearchLiveFieldsFinance';

//Functions
import QueryListener from '../../../../../Library/QueryListener';
import CollectionListener from '../../../../../Library/CollectionListener';

//Images
import EditIcon from '../../../../../Components/Images/Icon_Edit_Teal.svg';
import CloseIcon from '../../../../../Components/Images/Icon_Clear_Teal.svg';

//CSS


export default function FinalApproval({
    taskSection,
    task,
}) {

    //------------------------------------------------------
    //  useStates
    //------------------------------------------------------

    // Shows confirmation sidepane > true, false
    const [paneOpen, setPaneOpen] = useState(false);

    // Used to store the initial approval task
    const [initialApproval, setInitialApproval] = useState([]);

    // Used to store the final approvals
    const [finalApprovals, setFinalApprovals] = useState([]);

    // Used to store the list of final approvers
    const [finalApprovers, setFinalApprovers] = useState([]);

    // Holds state for finance manager editor
    const [editFinanceMgr, setEditFinanceMgr] = useState(false);

    //------------------------------------------------------
    //  Functions
    //------------------------------------------------------


    // Function used to check if the user can proceed to the next stage. Returns a list of tasks that are blocking the proceeding.
    // Will return empty array if tasks are fulfilled.
    function allowSubmit() {

        const blockersList = [];

        // Total Contract Value table is incomplete
        if (task?.costs === undefined || task?.costs?.length === 0) blockersList.push('You must have at least 1 entry in Total Contract Value');

        // Final Approvals already sent
        if (task?.sentfinalapprovals) blockersList.push('Final Approvals have been sent');

        // Initial approvals not approved
        if (initialApproval[0]?.status !== 'Completed') blockersList.push('Initial Approvals must be approved before proceeding');

        // Currently editing Finance Manager
        if (editFinanceMgr) blockersList.push('Finance Approver is in edit mode.')

        return blockersList;
    }

    //------------------------------------------------------
    //  useEffects
    //------------------------------------------------------

    // Query Listener
    // - Get the list of final approvals
    useEffect(() => {
      
        if (task === undefined) return;

        function onLoadChange(document) {

            // Get the Initial Approval
            const initialApprovalTask = document.filter((item) =>  item?.tasktype === 'Initial Approval');
            setInitialApproval(initialApprovalTask);

            // Sort Table final approvals consistently
            const finalApprovalTasks = [];

            finalApprovalTasks.push(document.find((item) => item?.tasktype === 'Finance Approval'));
            finalApprovalTasks.push(document.find((item) => item?.tasktype === 'Final Approval'));
            finalApprovalTasks.push(document.find((item) => item?.tasktype === 'COO Approval'));
            finalApprovalTasks.push(document.find((item) => item?.tasktype === 'LoA Approval'));

            setFinalApprovals(finalApprovalTasks);

        }
        
        function onError(error) {
          console.log('error', error);

        }
        
        const unsubscribe = QueryListener('requests', [
            ['parentrequestid', '==', task?.parentrequestid],
            ['tasktype', 'in', ['Initial Approval', 'Finance Approval', 'Final Approval', 'LoA Approval', 'COO Approval']]
        ], onLoadChange, onLoadChange, onError);
        
        return () =>{
          unsubscribe();
        };

    // eslint-disable-next-line
    }, [task])

    // Query Listener
    // - Get the list of final approvers before final approvals are sent
    useEffect(() => {

        if (task === undefined) return;
        if (task?.sentfinalapprovals === true) return;

        function onLoadChange(document) {

            // ----------------------------------------------------------
            // Get LoA Approver
            // ----------------------------------------------------------
            
            const loaRoleMapping = {
                'Investment Management': 'Digital IDC',
                'Investments': 'Digital IDC',
                'Development': 'Digital IDC',
                'Construction': 'Digital IDC',
                'Defence': 'Digital IDC',
                'Functions': 'Digital Functions',
                'Digital': 'Technology',
            };

            // Mapped LoA approver role for the user
            const loaRole = loaRoleMapping[task?.primaryusers];

            // LoA approver with matching role
            const loaApproverObj = document?.find(
                (obj) => obj.approvalbucket === 'LoA Approval' && obj.role === loaRole
            );

            // -------------------------------------------
            //  Get the Final Approvers
            // -------------------------------------------

            const allFinalApprovers = [

                // Finance Approver
                {
                    'email': task?.financemanager?.email,
                    'givenname': task?.financemanager?.givenname,
                    'surname': task?.financemanager?.surname,
                    'type': 'Finance Approval'
                },
                // Final BU Approver
                {
                    'email': document?.find((obj) => obj.approvalbucket === 'Final Approval' && obj.role === task?.businessunit)?.approveremail,
                    'givenname': document?.find((obj) => obj.approvalbucket === 'Final Approval' && obj.role === task?.businessunit)?.approvergivenname,
                    'surname': document?.find((obj) => obj.approvalbucket === 'Final Approval' && obj.role === task?.businessunit)?.approversurname,
                    'type': 'Final Approval',
                    'businessunit': document?.find((obj) => obj.approvalbucket === 'Final Approval' && obj.role === task?.businessunit)?.role
                }, 
                // COO Approver
                {
                    'email': document?.find((obj) => obj.approvalbucket === 'COO Approval')?.approveremail,
                    'givenname': document?.find((obj) => obj.approvalbucket === 'COO Approval')?.approvergivenname,
                    'surname': document?.find((obj) => obj.approvalbucket === 'COO Approval')?.approversurname,
                    'businessunit': 'COO',
                    'type': 'COO Approval',
                },
                // LoA Approver
                {
                    'email': loaApproverObj?.approveremail,
                    'givenname': loaApproverObj?.approvergivenname,
                    'surname': loaApproverObj?.approversurname,
                    'type': 'LoA Approval',
                    'businessunit': loaApproverObj?.role
                },
            ];

            setFinalApprovers(allFinalApprovers);

        }
        
        function onError(error) {
          console.log('error', error);

        }
        
        const unsubscribe = CollectionListener('approvers', onLoadChange, onLoadChange, onError);
        
        return () =>{
          unsubscribe();
        };

    // eslint-disable-next-line
    }, [task])

    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------

    return (
        <div className='p-[3%]' hidden={taskSection?.finalApprovalCollapsed}>

            <div className='w-full overflow-x-auto overflow-y-auto border border-[#D8D8D8] rounded px-[1%]'>
                <table className='w-full max-h-96 table-auto'>
                    <thead>
                        <tr className='text-[#424242] border-b border-b-[#D8D8D8]'>
                            <th className='py-[15px] px-[5px] font-medium'></th>
                            <th className='py-[15px] px-[10px] font-medium'>Type</th>
                            <th className='py-[15px] px-[10px] font-medium'>Business Unit</th>
                            <th className='py-[15px] px-[10px] font-medium'>Assigned To</th>
                            <th className='py-[15px] px-[10px] font-medium'>Comment</th>
                            <th className='py-[15px] px-[10px] font-medium'>Status</th>
                            <th className='py-[15px] px-[10px] font-medium'></th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            !task?.sentfinalapprovals ?

                            // Not Sent for Final Approval Yet
                            (
                                finalApprovers?.map((item, index) => (
                                    <tr key={index} className='border-b border-b-[#D8D8D8]'>

                                        <td className='py-[15px] px-[5px]'></td>

                                        {/* Type */}
                                        <td className='py-[15px] px-[10px]'>
                                            {item?.type}
                                        </td>

                                        {/* Business Unit */}
                                        <td className='py-[15px] px-[10px]'>
                                            {item?.businessunit?.length === 0 || item?.businessunit === undefined ? '--' : item?.businessunit}
                                        </td>

                                        {/* Assigned To */}
                                        <td className='py-[15px] px-[10px]'>

                                            {/* For Editing Finance Manager */}
                                            {item?.type === 'Finance Approval' ? 

                                            editFinanceMgr ?

                                                <div className='flex flex-row relative'>
                                                    <UserSearchLiveFieldsFinance
                                                        task={task}
                                                        defaultValue={item?.email}
                                                        disabled = {false}
                                                        setEditMode = {setEditFinanceMgr}
                                                    ></UserSearchLiveFieldsFinance>

                                                    {/* Cancel button */}
                                                    <img src={CloseIcon} alt='Clear' className='absolute right-0 py-[12px] pl-2 cursor-pointer' onClick={() => setEditFinanceMgr(false)}></img>
                                                </div>

                                                :

                                                <div
                                                    className='flex flex-row justify-between w-full px-[15px] py-[10px] bg-[white] border border-solid border-[#7c7c7c] rounded-[5px] cursor-pointer'
                                                    onClick={() => setEditFinanceMgr(true)}
                                                >
                                                    <div className='flex flex-col'>
                                                        <p className='m-0'>
                                                            {`${item?.givenname} ${item?.surname}`}
                                                        </p>
                                                        <p className='font-light m-0'>
                                                            {item?.email}
                                                        </p>
                                                    </div>
                                                    <img src={EditIcon} alt='Edit' className='-mt-4'></img>
                                                </div>
                                            
                                            :

                                            <div className='flex flex-col'>
                                                <p className='m-0'>
                                                    {`${item?.givenname} ${item?.surname}`}
                                                </p>
                                                <p className='font-light m-0'>
                                                    {item?.email}
                                                </p>
                                            </div>

                                            }
                                        </td>

                                        {/* Comment */}
                                        <td className='py-[15px] px-[10px]'>
                                            --
                                        </td>

                                        {/* Status */}
                                        <td className='py-[15px] px-[5px]'>
                                            <StatusLabel status={task?.status === 'Cancelled' ? 'Cancelled' : 'Not Started'}></StatusLabel>
                                        </td>

                                        <td className='py-[15px] px-[5px]'></td>

                                    </tr>
                                ))   

                            )

                            : finalApprovals?.length === 0 || finalApprovals === undefined ?

                            // Sent For Final Approval > No Approvals Found
                            (
                                <tr>
                                    <td className='border-t border-t-[#D8D8D8]' colSpan={7}>
                                        <div className='text-center p-[2%]'>
                                            No approvals found.
                                        </div>
                                    </td>
                                </tr>
                            )
                            :
                            // Sent For Final Approval > Final Approvals Table
                            (
                                finalApprovals?.map((item, index) => (
                                    <tr key={index} className='border-b border-b-[#D8D8D8]'>

                                        <td className='py-[15px] px-[5px]'></td>

                                        {/* Type */}
                                        <td className='py-[15px] px-[10px]'>
                                            {item?.tasktype}
                                        </td>

                                        {/* Business Unit */}
                                        <td className='py-[15px] px-[10px]'>
                                            {item?.approverrole?.length === 0 || item?.approverrole === undefined ? '--' : item?.approverrole}
                                        </td>

                                        {/* Assigned To */}
                                        <td className='py-[15px] px-[10px]'>
                                            <div className='flex flex-col'>
                                                <p className='m-0'>
                                                    {`${item?.assignedto?.givenname} ${item?.assignedto?.surname}`}
                                                </p>
                                                <p className='font-light m-0'>
                                                    {item?.assignedto?.email}
                                                </p>
                                            </div>
                                        </td>

                                        {/* Comment */}
                                        <td className='py-[15px] px-[10px]'>
                                            {item?.comments?.length > 0 ? item?.comments : '--'}
                                        </td>

                                        {/* Status */}
                                        <td className='py-[15px] px-[5px]'>
                                            <StatusLabel status={item?.status} tasktype={item?.tasktype}></StatusLabel>
                                        </td>

                                        <td className='py-[15px] px-[5px]'></td>

                                    </tr>
                                ))
                            )
                        }
                    </tbody>
    
                </table>
            </div>

            <br></br>

            {/* Submit For Final Approval Button */}
            <div className='flex flex-col gap-2 place-items-end'>
                <button 
                    className='Primary-Button' 
                    onClick={() => setPaneOpen(true)} 
                    disabled={allowSubmit().length > 0}
                > 
                    {
                    allowSubmit().length === 0 ?
                        'Submit for Final Approval' :
                        <Tooltip 
                            children={`${task?.sentfinalapprovals ? 'Submitted' : 'Submit'} for Final Approval`}
                            message={
                                <ul className='list-disc text-left'>
                                    {allowSubmit().map((blocker, index) => (
                                        <li key={index}>{blocker}</li>
                                    ))}
                                </ul>
                            }
                        ></Tooltip>
                    }
                </button>
                <p className='font-light italic'>Total Contract Value must be finalised prior to submitting for final approval.</p>
            </div>

            {/* -------------------------------------- */}
            {/*  Submit for Final Approval Sidepane    */}
            {/* -------------------------------------- */}

            <SubmitFinalApprovalPane
                paneOpen={paneOpen}
                setPaneOpen={setPaneOpen}
                task={task}
                finalApprovers={finalApprovers}
            ></SubmitFinalApprovalPane>

        </div>
    )
}